import { FileSpreadsheet, Printer } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./DayBook.css";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "https://payyug.in/api";
const PAGE_LIMIT = 200;

const DayBook = () => {
  const [date, setDate] = useState("");
  const [appliedDate, setAppliedDate] = useState("");

  const [dayBookData, setDayBookData] = useState([]);
  const [totalDebit, setTotalDebit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);

  const [maxLimit, setMaxLimit] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  /* ================= FETCH ================= */
  const fetchDayBookData = useCallback(async () => {
    if (!appliedDate) return;

    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const superAdminId = localStorage.getItem("adminuserId");

      const res = await axios.get(`${API_URL}/report/day-book`, {
        params: {
          superAdminId,
          branchId: 66,
          date: appliedDate,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const vouchers = res?.data?.vouchers || [];

      setDayBookData(vouchers);
      setTotalDebit(res?.data?.totalDebit || 0);
      setTotalCredit(res?.data?.totalCredit || 0);

      setMaxLimit(Math.min(vouchers.length, PAGE_LIMIT));
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to fetch day book");
    } finally {
      setLoading(false);
    }
  }, [appliedDate]);

  /* ================= FILTER ================= */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!date) {
      toast.error("Please select Date");
      return;
    }

    setAppliedDate(date);
    setHasSearched(true);
  };

  /* ================= FETCH AFTER SEARCH ================= */
  useEffect(() => {
    if (hasSearched) fetchDayBookData();
  }, [fetchDayBookData, hasSearched]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(dayBookData.length / PAGE_LIMIT);

  const paginatedData = useMemo(() => {
    if (dayBookData.length <= PAGE_LIMIT) return dayBookData;

    const start = (currentPage - 1) * PAGE_LIMIT;
    const end = start + PAGE_LIMIT;
    return dayBookData.slice(start, end);
  }, [dayBookData, currentPage]);

  /* ================= HELPERS ================= */
  const splitEntries = (entries = []) => ({
    drEntries: entries.filter((e) => e.factor === "dr"),
    crEntries: entries.filter((e) => e.factor === "cr"),
  });

const remainingCount = Math.max(
  PAGE_LIMIT - dayBookData.length,
  0
);



  return (
    <div className="dayBook-container">
      <h1 className="dayBook-heading">Day Book</h1>

      {/* FILTER */}
      <div className="dayBook-top">
        <form className="dayBook-form" onSubmit={handleSubmit}>
          <div className="dayBook-form-section">
            <div className="dayBook-form-input-fields">
              <label>Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>

          <div className="dayBook-form-submit-btn">
            <button type="submit">
              {loading ? "Loading..." : "Get Records"}
            </button>
          </div>
        </form>
      </div>

      {/* DOWNLOAD */}
      <div className="dayBook-pdf-download-btns">
        <button>
          <FileSpreadsheet size={15} /> Download Excel
        </button>
        <button>
          <Printer size={15} /> Print
        </button>
      </div>

      {/* REPORT */}
      <div className="dayBook-report-section">
        <h2>Ledgers</h2>
        <p className="dayBook-record-limit">
          MAXIMUM RECORDS SHOW LIMIT IS {PAGE_LIMIT} | REMAINING {remainingCount}
        </p>

        <div className="dayBook-report-table">
          <table>
            <thead>
              <tr>
                <th>SN.</th>
                <th>Date</th>
                <th>Voucher No</th>
                <th>Voucher Type</th>
                <th>Narration</th>
                <th className="amount-head" style={{ width: "200px" }}>
                  <div className="dayBook-th-data">
                    <h4>Amount</h4>
                    <div className="dayBook-th-amount">
                      <span>DR</span>
                      <span>CR</span>
                    </div>
                  </div>
                </th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {!hasSearched ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    Please select filters and click Get Records
                  </td>
                </tr>
              ) : paginatedData.length ? (
                paginatedData.map((item, index) => {
                  const { drEntries, crEntries } = splitEntries(item.entries);

                  return (
                    <tr key={item.id}>
                      <td>
                        {(currentPage - 1) * PAGE_LIMIT + index + 1}
                      </td>
                      <td>
                        {item.date
                          ? new Date(item.date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td>{item.voucherNo}</td>
                      <td>{item.voucherType}</td>
                      <td>{item.narration}</td>

                      <td className="amount-row">
                        <span>
                          {drEntries.length
                            ? drEntries.map((e, i) => (
                                <div key={i}>{e.debit}</div>
                              ))
                            : "0"}
                        </span>
                        <span>
                          {crEntries.length
                            ? crEntries.map((e, i) => (
                                <div key={i}>{e.credit}</div>
                              ))
                            : "0"}
                        </span>
                      </td>

                      <td>{item.status || "-"}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    No results found
                  </td>
                </tr>
              )}

              {hasSearched && dayBookData.length > 0 && (
                <tr className="totalRow">
                  <td colSpan="5">TOTAL AMOUNT</td>
                  <td>
                    <div className="dayBook-th-amount">
                      <p>{totalDebit}</p>
                      <p>{totalCredit}</p>
                    </div>
                  </td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>

          {/* PAGINATION (ONLY IF > 200) */}
          {dayBookData.length > PAGE_LIMIT && (
            <div className="pagination">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Prev
              </button>

              <span>
                Page {currentPage} of {totalPages}
              </span>

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}

          {error && <p className="error-text">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default DayBook;
